import React from 'react';
import { ParticipantHubLayout } from './ParticipantHubLayout';
import { motion } from 'framer-motion';
import { TargetIcon, CalendarIcon, UsersIcon, PlusIcon, CheckCircle2Icon, CircleIcon, ArrowRightIcon, BookOpenIcon } from 'lucide-react';
export function ActionPlanPage() {
  return <ParticipantHubLayout data-id="element-4531">
      <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-8" data-id="element-4532">
        <div data-id="element-4533">
          <h1 className="font-serif-display text-4xl text-slate-900 mb-2" data-id="element-4534">
            My Action Plan
          </h1>
          <p className="text-slate-500" data-id="element-4535">
            Your commitments and integration strategy post-summit.
          </p>
        </div>

        {/* Hero Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-[#0B1B3B] rounded-2xl p-8 text-white relative overflow-hidden shadow-lg" data-id="element-4536">
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-4537" />
          <div className="relative z-10" data-id="element-4538">
            <div className="flex items-center gap-2 text-amber-400 text-sm font-bold tracking-wider uppercase mb-4" data-id="element-4539">
              <TargetIcon className="w-4 h-4" data-id="element-4540" />
              90-Day Integration
            </div>
            <h2 className="font-serif-display text-3xl mb-4" data-id="element-4541">
              Your generated plan is taking shape.
            </h2>
            <p className="text-slate-300 max-w-2xl mb-6" data-id="element-4542">
              Based on your notes and session participation, we've drafted 3 key
              commitments. Review and finalize them before the closing session.
            </p>
            <div className="flex items-center gap-4" data-id="element-4543">
              <div className="flex -space-x-2" data-id="element-4544">
                <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-[#0B1B3B] z-20 overflow-hidden" data-id="element-4545">
                  <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=100&q=80" alt="Peer" className="w-full h-full object-cover" data-id="element-4546" />
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-300 border-2 border-[#0B1B3B] z-10 overflow-hidden" data-id="element-4547">
                  <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=100&q=80" alt="Peer" className="w-full h-full object-cover" data-id="element-4548" />
                </div>
              </div>
              <span className="text-sm text-slate-300" data-id="element-4549">
                Shared with your accountability pod
              </span>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-4550">
          {/* Commitments List */}
          <div className="lg:col-span-2 space-y-6" data-id="element-4551">
            <div className="flex items-center justify-between" data-id="element-4552">
              <h3 className="font-serif-display text-2xl text-slate-900" data-id="element-4553">
                Key Commitments
              </h3>
              <button className="text-sm font-medium text-amber-600 flex items-center gap-1 hover:text-amber-700" data-id="element-4554">
                <PlusIcon className="w-4 h-4" data-id="element-4555" />
                Add Custom
              </button>
            </div>

            <div className="space-y-4" data-id="element-4556">
              {[{
              title: 'Implement AI Code Review Pilot',
              desc: 'Roll out the new AI review tool to the core platform team for a 30-day trial.',
              deadline: 'Nov 15, 2026',
              status: 'draft'
            }, {
              title: 'Cross-functional Sync with Product',
              desc: 'Establish bi-weekly alignment meetings with Elena to ensure roadmap synergy.',
              deadline: 'Oct 30, 2026',
              status: 'active'
            }, {
              title: 'Complete Leadership Assessment 360',
              desc: 'Finalize the post-retreat feedback loop with direct reports.',
              deadline: 'Nov 1, 2026',
              status: 'active'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 + i * 0.1
            }} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow flex gap-4" data-id="element-4557">
                  <button className="mt-1 shrink-0" data-id="element-4558">
                    {item.status === 'done' ? <CheckCircle2Icon className="w-6 h-6 text-emerald-500" data-id="element-4559" /> : <CircleIcon className="w-6 h-6 text-slate-300 hover:text-amber-500 transition-colors" data-id="element-4560" />}
                  </button>
                  <div className="flex-1" data-id="element-4561">
                    <div className="flex items-start justify-between mb-1" data-id="element-4562">
                      <h4 className="font-bold text-slate-900" data-id="element-4563">{item.title}</h4>
                      {item.status === 'draft' && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded" data-id="element-4564">
                          Draft
                        </span>}
                    </div>
                    <p className="text-sm text-slate-600 mb-3" data-id="element-4565">{item.desc}</p>
                    <div className="flex items-center gap-4 text-xs font-medium text-slate-500" data-id="element-4566">
                      <div className="flex items-center gap-1" data-id="element-4567">
                        <CalendarIcon className="w-3.5 h-3.5" data-id="element-4568" />
                        Due: {item.deadline}
                      </div>
                      <button className="text-slate-400 hover:text-slate-600" data-id="element-4569">
                        Edit
                      </button>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-4570">
            {/* Peer Check-in */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-4571">
              <div className="flex items-center gap-2 mb-4" data-id="element-4572">
                <UsersIcon className="w-5 h-5 text-amber-500" data-id="element-4573" />
                <h3 className="font-bold text-slate-900" data-id="element-4574">Peer Check-in</h3>
              </div>
              <p className="text-sm text-slate-600 mb-4" data-id="element-4575">
                You are paired with <strong data-id="element-4576">Marcus Chen</strong> for post-summit
                accountability.
              </p>
              <button className="w-full py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-4577">
                Schedule 1:1 Sync
              </button>
            </motion.div>

            {/* Reflection Journal */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="bg-amber-50 rounded-2xl border border-amber-100 p-6 shadow-sm" data-id="element-4578">
              <div className="flex items-center gap-2 mb-4" data-id="element-4579">
                <BookOpenIcon className="w-5 h-5 text-amber-600" data-id="element-4580" />
                <h3 className="font-bold text-slate-900" data-id="element-4581">Daily Reflection</h3>
              </div>
              <textarea placeholder="What was your biggest insight today?" className="w-full h-24 bg-white border border-amber-200 rounded-xl p-3 text-sm resize-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none mb-3 placeholder:text-slate-400" data-id="element-4582" />
              <button className="w-full py-2 bg-amber-600 text-white rounded-xl text-sm font-bold hover:bg-amber-700 transition-colors" data-id="element-4583">
                Save Entry
              </button>
            </motion.div>

            {/* Outcomes Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6
          }} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-4584">
              <h3 className="font-bold text-slate-900 mb-2" data-id="element-4585">
                Organizational Impact
              </h3>
              <p className="text-xs text-slate-500 mb-4" data-id="element-4586">
                Your commitments roll up into the Finova Capital Outcomes
                Dashboard, helping measure the ROI of this summit.
              </p>
              <button className="text-sm text-amber-600 font-medium flex items-center gap-1 hover:text-amber-700" data-id="element-4587">
                View Team Dashboard <ArrowRightIcon className="w-4 h-4" data-id="element-4588" />
              </button>
            </motion.div>
          </div>
        </div>
      </div>
    </ParticipantHubLayout>;
}